/** Corpo di POST /api/auth/logout/end-session (sso-bck: routes/AuthRoute.kt). */
export interface EndSessionRequest {
  /**
   * Riferimento opaco alla richiesta di logout in corso, generato da sso-bck e passato a
   * questo FE come query param. Scade in 180s ed è monouso: è lui a portare l'autorità,
   * non il chiamante, ed è il motivo per cui questa rotta sta fuori da `authenticate`.
   */
  opaque: string;
  /** Falso quando l'utente annulla: la sessione resta, ma il giro si chiude comunque. */
  confirmed: boolean;
}

export interface EndSessionResponse {
  /**
   * Pagina di atterraggio dell'applicativo, con `state` se era stato inviato.
   * `null` quando l'applicativo non ha indicato un `post_logout_redirect_uri`: in quel
   * caso non c'è nessun posto dove tornare e il giro finisce qui.
   */
  redirectUri: string | null;
}

/**
 * Cosa dire per ciascun codice che sso-bck passa alla pagina di errore del logout come
 * `?error=` (errors/SSOError.kt, `LogoutRedirect.Code`, in minuscolo).
 *
 * `retry` distingue i due casi che non vanno confusi: `invalid_request` può dipendere da
 * una richiesta malformata o scaduta, e riprovare ha senso; `invalid_client` e
 * `invalid_redirect_uri` sono errori di **configurazione** dell'applicativo, e riprovare
 * mille volte darà mille volte lo stesso esito. Suggerire "riprova" in quei casi manda
 * l'utente a sbattere.
 */
export interface LogoutErrorInfo {
  cause: string;
  retry: boolean;
}

const LOGOUT_ERROR = new Map<string, LogoutErrorInfo>([
  ['invalid_request', { cause: 'La richiesta di uscita non è valida o è scaduta.', retry: true }],
  [
    'invalid_client',
    {
      cause: "L'applicativo che ha richiesto l'uscita non è riconosciuto dall'SSO.",
      retry: false,
    },
  ],
  [
    'invalid_redirect_uri',
    {
      cause: "L'indirizzo di ritorno non è fra quelli registrati per questo applicativo.",
      retry: false,
    },
  ],
]);

/** Esito ignoto: si resta prudenti e non si promette che riprovare aiuti. */
export const LOGOUT_ERROR_UNKNOWN: LogoutErrorInfo = {
  cause: "La richiesta di uscita non è stata accettata dall'SSO.",
  retry: false,
};

/**
 * Traduce il `?error=` in un messaggio, dicendo anche se il codice era previsto.
 *
 * Il codice arriva dalla query string, quindi lo decide chi apre l'indirizzo: non va
 * ripetuto a schermo se non è uno dei nostri. Questa pagina è servita dall'origin dell'SSO
 * ed è l'ultimo posto in cui vada mostrato del testo scelto da altri — è la stessa ragione
 * per cui sso-bck manda qui solo un codice e non la descrizione (`SSOError.LogoutRedirect`).
 *
 * La ricerca passa da una `Map` e non da un oggetto: su un `Record` la chiave `constructor`
 * restituirebbe una funzione presa dal prototipo, che poi la pagina proverebbe a leggere
 * come un messaggio.
 */
export function logoutErrorFor(code: string | null): {
  info: LogoutErrorInfo;
  knownCode: string | null;
} {
  const info = code !== null ? LOGOUT_ERROR.get(code) : undefined;
  return info !== undefined
    ? { info, knownCode: code }
    : { info: LOGOUT_ERROR_UNKNOWN, knownCode: null };
}
